import { useState } from 'react';
import { Link } from 'react-router';
import { authClient } from '../lib/auth-client';

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const redirectTo = `${window.location.origin}/reset-password`;
      const result = await authClient.requestPasswordReset({ email, redirectTo });
      if (result.error) {
        setError(result.error.message || 'Could not send reset email');
        setLoading(false);
        return;
      }
      setSubmitted(true);
      setLoading(false);
    } catch {
      setError('An unexpected error occurred');
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-[var(--color-surface-container-low)] px-6">
      <div className="w-full max-w-sm space-y-8">
        <div className="text-center">
          <h1 className="display-tight text-3xl font-semibold">Forgot Password</h1>
          <p className="mt-2 text-sm text-muted-foreground">
            We'll send you a link to reset it
          </p>
        </div>

        <div className="rounded-3xl bg-card p-8 shadow-ambient">
          {submitted ? (
            <div className="space-y-4 text-center">
              <p className="text-sm">
                If an account exists for <strong>{email}</strong>, a reset link is on its way.
                Check your inbox (and spam folder).
              </p>
              <Link
                to="/login"
                className="inline-block text-sm font-medium text-secondary underline-offset-4 hover:underline"
              >
                Back to sign in
              </Link>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-5">
              {error && (
                <div className="rounded-2xl bg-destructive/10 p-3 text-sm text-destructive">
                  {error}
                </div>
              )}

              <div className="space-y-2">
                <label htmlFor="email" className="label-caps text-xs text-muted-foreground">
                  Email
                </label>
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="flex h-10 w-full rounded-2xl border border-[var(--color-outline-variant)] bg-[var(--color-surface-container-lowest)] px-4 py-2 text-sm transition-all placeholder:text-muted-foreground focus:bg-[var(--color-surface-bright)] focus:shadow-[0_4px_16px_rgba(49,51,47,0.06)] focus:outline-none"
                  placeholder="you@example.com"
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                className="btn-primary-gradient inline-flex h-11 w-full items-center justify-center rounded-full px-6 text-sm font-medium text-white shadow-ambient transition-opacity hover:opacity-90 disabled:pointer-events-none disabled:opacity-50"
              >
                {loading ? 'Sending...' : 'Send reset link'}
              </button>
            </form>
          )}
        </div>

        <p className="text-center text-sm text-muted-foreground">
          Remembered it?{' '}
          <Link to="/login" className="font-medium text-secondary underline-offset-4 hover:underline">
            Sign in
          </Link>
        </p>
      </div>
    </div>
  );
}
